import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useSEO } from "../../lib/seo";
import { ArrowRight, MapPin, CurrencyDollar, Star } from "@phosphor-icons/react";

export default function VirtualServiceLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/virtual/seo/${slug}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [slug]);

  useSEO(data ? {
    title: data.page.title,
    description: data.page.intro,
    path: `/virtual/services/${slug}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: data.page.title,
      description: data.page.intro,
      provider: { "@type": "Organization", name: "CanHireSolution.com" },
      areaServed: { "@type": "Country", name: "Canada" },
    },
  } : { title: "Loading…" });

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!data) return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-2xl mx-auto px-4 text-center">
        <h1 className="font-display text-4xl mb-3">Page not found</h1>
        <Link to="/virtual" className="text-brand font-semibold">← Hire Virtual Staff</Link>
      </div>
      <Footer />
    </div>
  );

  const { page, profiles = [], jobs = [] } = data;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-12 bg-brand text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/virtual" className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70 hover:text-white">← Hire Virtual Staff</Link>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 mb-4">{page.title}</h1>
          <p className="text-base sm:text-lg text-white/85 max-w-2xl">{page.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/virtual/freelancers?service=${encodeURIComponent(page.service)}`} className="bg-accent-red text-white px-5 py-3 font-semibold hover:bg-[#D85A1A]" data-testid="vsl-browse-btn">Browse freelancers</Link>
            <Link to="/virtual/jobs/new" className="border border-white/40 text-white px-5 py-3 font-semibold hover:bg-white/10" data-testid="vsl-post-btn">Post a project</Link>
          </div>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-2xl mb-6">Top freelancers</h2>
            {profiles.length === 0 ? (
              <p className="text-sm text-slate-500">No freelancers listed yet. <Link to="/virtual/freelancer/profile" className="text-brand font-semibold">Be the first to register</Link>.</p>
            ) : (
              <div className="space-y-3">
                {profiles.map((p) => (
                  <Link key={p.user_id} to={`/virtual/freelancers/${p.user_id}`} className="block border border-slate-200 p-4 hover:border-brand transition-colors" data-testid={`vsl-profile-${p.user_id}`}>
                    <div className="flex items-center gap-3 mb-1">
                      {p.picture ? <img src={p.picture} alt={p.name} className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 bg-slate-200 rounded-full" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="text-xs text-slate-500 truncate">{p.headline}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 text-xs text-slate-500 mt-2">
                      <span className="flex items-center gap-1"><MapPin size={12} /> {p.country}</span>
                      <span className="flex items-center gap-1"><CurrencyDollar size={12} /> ${p.hourly_rate}/hr</span>
                      {p.rating > 0 && <span className="flex items-center gap-1"><Star size={12} weight="fill" className="text-accent-red" /> {p.rating.toFixed(1)}</span>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-display text-2xl mb-6">Open projects</h2>
            {jobs.length === 0 ? (
              <p className="text-sm text-slate-500">No open projects right now. <Link to="/virtual/jobs/new" className="text-brand font-semibold">Post the first one</Link>.</p>
            ) : (
              <div className="space-y-3">
                {jobs.map((j) => (
                  <Link key={j.job_id} to={`/virtual/jobs/${j.job_id}`} className="block border border-slate-200 p-4 hover:border-brand transition-colors" data-testid={`vsl-job-${j.job_id}`}>
                    <div className="font-display text-lg mb-1">{j.title}</div>
                    <div className="text-xs text-slate-500 line-clamp-1 mb-2">{j.description}</div>
                    <div className="text-xs text-slate-700 font-semibold">${j.budget_min}–${j.budget_max} CAD · {j.pricing_type}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 bg-slate-50 border border-slate-200 p-8 text-center">
          <h3 className="font-display text-2xl mb-3">Ready to start?</h3>
          <p className="text-sm text-slate-600 max-w-xl mx-auto mb-5">Post a project for free. We&apos;ll only take 10% when work is completed — no upfront fees, no subscriptions required.</p>
          <Link to="/virtual/jobs/new" className="inline-flex items-center gap-2 bg-brand text-white px-6 py-3 font-semibold hover:bg-[#122148]">
            Post your project <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
